
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Eye, Download, FileText, User, Calendar, DollarSign, Percent, Clock, Building2, CreditCard, Shield, Banknote, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { CalculationInputs, CalculationSummary } from '../../utils/calculationUtils';
import apiService from '../../services/apiService';
import { Textarea } from '../../components/ui/textarea';
import TermsheetDrawer from './TermsheetDrawer';
interface DownloadedTermSheet {
  id: string;
  calculationId: string;
  termSheetId?: string;
  calculationName: string;
  downloadedAt: Date;
  downloadedBy: string;
  inputs: CalculationInputs;
  summary: CalculationSummary;
  status: 'review' | 'rejected' | 'approved';
  approvedAt?: Date;
  approvedBy?: string;
  remarks?: string;
}

interface TermSheetHistoryProps {
  termSheets: DownloadedTermSheet[];
  onUpdateTermSheet?: (termSheet: DownloadedTermSheet) => void;
  onUpdateCalculation?: (calculation: DownloadedTermSheet) => void;
  customerId?: string;
}
interface ApiResponse<T> {
  data: T;
  downloaded: T;
  message?: string;
  status?: number;
}

const TermSheetHistory: React.FC<TermSheetHistoryProps> = ({ termSheets, onUpdateTermSheet ,customerId}) => {
  const { toast } = useToast();
  const [selectedTermSheet, setSelectedTermSheet] = useState<DownloadedTermSheet | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [remark, setRemark] = useState('');
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyCity, setCompanyCity] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const formatCompactCurrency = (value: number) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const formatLabelRentalCalculationBasis = (key: string) => {
    return key
      .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space before capital letters
      .replace(/^./, (s) => s.toUpperCase()) // Capitalize first letter
      .replace(/\bGst\b/, 'GST') // Optional: force uppercase GST
      .replace(/\bTds\b/, 'TDS'); // Optional: force uppercase TDS
  }

  useEffect(() => {
    const storedCustomer = sessionStorage.getItem("selectedCustomer");
          if (storedCustomer) {
              const parsed = JSON.parse(storedCustomer);
              setCompanyName(parsed.companyName || parsed.name);
              setCompanyCity(parsed.city || null); 
          }
        }, []);


  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleRemarkSave = (remark: string) => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        await apiService.post<ApiResponse<any>>(
          `/customers/update-term-sheet-remark/${selectedTermSheet?.id}`,
          { remark }
        );

        if (selectedTermSheet && onUpdateTermSheet) {
          const updatedTermSheet = { ...selectedTermSheet, remarks: remark };
          onUpdateTermSheet(updatedTermSheet);
          setSelectedTermSheet(updatedTermSheet);
        }

      } catch (error) {
        console.error("Failed to update remark:", error);
      }
    }, 800); // adjust debounce delay as needed
  };

  const handleViewClick = (termSheet: DownloadedTermSheet) => {
    setSelectedTermSheet(termSheet);
    setRemark(termSheet.remarks || '');
    setIsViewOpen(true);
  };

  const handleDownloadClick = (termSheet: DownloadedTermSheet) => {
    setSelectedTermSheet(termSheet);
    setRemark(termSheet.remarks || '');
    setIsSheetOpen(true);
  };

  if (termSheets.length === 0) {
    return (
      <Card className="h-96">
        <CardContent className="h-full flex items-center justify-center">
          <div className="text-center p-8">
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Term Sheets Generated</h3>
            <p className="text-gray-500 max-w-md">Generate term sheets from your saved calculations to view them here.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Term Sheet History</h2>
          <p className="text-gray-600 mt-1">Track and manage your downloaded term sheets</p>
        </div>
        <div className="text-sm text-gray-600">
          {termSheets.length} {termSheets.length === 1 ? 'Term Sheet' : 'Term Sheets'}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">Term Sheet History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b">
                  <TableHead className="font-semibold text-gray-700">Calculation ID</TableHead>
                  <TableHead className="font-semibold text-gray-700">TS - ID</TableHead>
                  <TableHead className="font-semibold text-gray-700">Generated at</TableHead>
                  <TableHead className="font-semibold text-gray-700">Generated by</TableHead>
                  <TableHead className="font-semibold text-gray-700">PTPM</TableHead>
                  <TableHead className="font-semibold text-gray-700">Disbursement amount</TableHead>
                  <TableHead className="font-semibold text-gray-700">Margin</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {termSheets.map((termSheet) => (
                  <TableRow key={termSheet.id} className="hover:bg-gray-50">
                    <TableCell className="font-mono text-sm font-medium">
                      {termSheet.calculationId}
                    </TableCell>
                    <TableCell className="font-mono text-sm font-medium">
                      {termSheet.termSheetId}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(termSheet.downloadedAt)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {termSheet.downloadedBy}
                    </TableCell>
                    <TableCell className="font-medium">
                      {termSheet.inputs?.finalPTPM}
                    </TableCell>
                    <TableCell className="font-medium">
                      {termSheet.inputs.disbursementAmount || 0}
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${termSheet.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'
                        }`}>
                        {Number(termSheet.summary.marginPercentage).toFixed(2)}%
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        termSheet.status === 'approved' ? 'default' :
                          termSheet.status === 'rejected' ? 'destructive' : 'secondary'
                      }>
                        {termSheet.status.charAt(0).toUpperCase() + termSheet.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewClick(termSheet)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadClick(termSheet)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Sidebar */}
      <Sheet open={isViewOpen} onOpenChange={setIsViewOpen}>
        <SheetContent className="w-[600px] sm:w-[800px] overflow-y-auto">
          <SheetHeader className="pb-6">
            <SheetTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-[#f8f6f1] rounded-lg">
                <FileText className="h-6 w-6 text-[#9e7926]" />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900">Term Sheet Details</div>
                <div className="text-sm text-gray-500 font-mono">{selectedTermSheet ? selectedTermSheet?.termSheetId : ''}</div>
              </div>
            </SheetTitle>
          </SheetHeader>
          {selectedTermSheet && (
            <div className="space-y-8">
              {/* Basic Information */}
              <div className="bg-[#f8f6f1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-[#9e7926]" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <FileText className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Calculation ID</Label>
                      <div className="font-mono font-semibold text-gray-900">{selectedTermSheet.calculationId}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <FileText className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Termsheet ID</Label>
                      <div className="font-mono font-semibold text-gray-900">{selectedTermSheet.termSheetId}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Calendar className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Generated at</Label>
                      <div className="font-semibold text-gray-900">{formatDate(selectedTermSheet.downloadedAt)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Generated by</Label>
                      <div className="font-semibold text-gray-900">{selectedTermSheet.downloadedBy}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Asset & GST Information */}
              <div className="bg-[#f8f6f1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-[#9e7926]" />
                  Asset & Tax Information
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <DollarSign className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Asset Value</Label>
                      <div className="font-semibold text-gray-900">{formatCurrency(selectedTermSheet.inputs.totalRental)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Percent className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">GST Rate</Label>
                      <div className="font-semibold text-gray-900">{(selectedTermSheet.inputs.gstRate * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rental Terms */}
              <div className="bg-[#f8f6f1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-[#9e7926]" />
                  Rental Terms
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Clock className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Frequency</Label>
                      <div className="font-semibold text-gray-900 capitalize">{selectedTermSheet.inputs.rentalFrequency}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Calendar className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Tenure</Label>
                      <div className="font-semibold text-gray-900">{selectedTermSheet.inputs.totalMonths} Months</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <DollarSign className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">PTPM</Label>
                      <div className="font-semibold text-gray-900">{selectedTermSheet.inputs.ptpmConsideration}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                                      <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <DollarSign className="h-4 w-4 text-gray-600" />
                                      </div>
                                      <div>
                                        <Label className="text-sm text-gray-600">Final PTPM</Label>
                                        <div className="font-semibold text-gray-900">{selectedTermSheet?.inputs?.finalPTPM}</div>
                                      </div>
                                      
                                    </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Percent className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">GST Passback</Label>
                      <Badge variant={selectedTermSheet.inputs.gstPassbackEnabled ? "default" : "secondary"} className="ml-2">
                        {selectedTermSheet.inputs.gstPassbackEnabled ? "Enabled" : "Disabled"}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Percent className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Discounting Factor</Label>
                      <div className="font-semibold text-gray-900">{(selectedTermSheet.inputs.discountingFactor * 100).toFixed(2)}%</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Clock className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Rental Calculation Basis</Label>
                      <div className="font-semibold text-gray-900 capitalize">{formatLabelRentalCalculationBasis(selectedTermSheet.inputs.rentalCalculationBasis)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Details */}


              <div className="bg-[#f8f6f1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-[#9e7926]" />
                  Financial Details
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Shield className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Security Deposit</Label>
                      <div className="font-semibold text-gray-900">{formatCurrency(selectedTermSheet?.summary?.securityDeposit)}  ({selectedTermSheet?.inputs?.securityDepositPercentage}%) </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <CreditCard className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Processing Fee</Label>
                      <div className="font-semibold text-gray-900">{formatCurrency(selectedTermSheet?.summary?.processingFee)}   ({selectedTermSheet?.inputs?.processingFeePercentage}%) </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Banknote className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Residual Value</Label>
                      <div className="font-semibold text-gray-900">{formatCurrency(selectedTermSheet?.inputs?.refundableSecurity || 0)}    ({selectedTermSheet?.inputs?.refundableSecurityPercentage}%) </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Information */}
              <div className="bg-[#f8f6f1] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-[#9e7926]" />
                  Summary Information
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <DollarSign className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Disbursement Amount</Label>
                      <div className="font-semibold text-gray-900">{selectedTermSheet.inputs.disbursementAmount || 0}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <DollarSign className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Margin</Label>
                      <div className={`font-semibold ${selectedTermSheet.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(selectedTermSheet.summary.marginAvailable)}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Percent className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">Margin Percentage</Label>
                      <div className={`font-semibold ${selectedTermSheet.summary.isNegativeMargin ? 'text-red-600' : 'text-green-600'}`}>
                        {Number(selectedTermSheet.summary.marginPercentage).toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Remark Section */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-5 w-5 text-gray-600" />
                  <Label className="text-lg font-semibold text-gray-900">Remark</Label>
                </div>
                <div className="flex gap-3">




                  <Textarea
                    value={remark}
                    onChange={(e) => {
                      const value = e.target.value;
                      setRemark(value); // ✅ Update local state so it shows what you type
                      handleRemarkSave(value); // ✅ Save with debounce
                    }}
                    placeholder="Add your remark here..."
                    className="flex-1 h-[100px]"
                  />

                  {/* <Button onClick={handleSaveRemark} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                    <Save className="h-4 w-4" />
                    Save
                  </Button> */}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Term Sheet Preview - Drawer lives inside TermsheetDrawer */}
      {isSheetOpen && selectedTermSheet && (
        <TermsheetDrawer
          drawerOpen={isSheetOpen}
          setDrawerOpen={setIsSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setSelectedTermSheet(null);
          }}
          selectedCalculation={selectedTermSheet}
          companyName={companyName || ""}
          companyCity={companyCity || ""}
          customerId={customerId}
          onSaveSuccess={() => {}}
          termSheetId={selectedTermSheet.termSheetId}
          // optionalHeaderSlot={
          //   <div className="flex flex-row items-center justify-between mr-[2%] p-4 border-b border-border">
          //     <span className="text-lg font-semibold">Term Sheet - {selectedTermSheet.termSheetId}</span>
          //     <Select onValueChange={(value) => handleStatusUpdate(selectedTermSheet, value)}>
          //       <SelectTrigger className="w-32">
          //         <SelectValue placeholder={
          //           <Badge variant={
          //             selectedTermSheet.status === 'approved' ? 'default' :
          //               selectedTermSheet.status === 'rejected' ? 'destructive' : 'secondary'
          //           }>
          //             {selectedTermSheet.status.charAt(0).toUpperCase() + selectedTermSheet.status.slice(1)}
          //           </Badge>
          //         } />
          //       </SelectTrigger>
          //       <SelectContent className='bg-white' >
          //         <SelectItem value="review">Review</SelectItem>
          //         <SelectItem value="rejected">Rejected</SelectItem>
          //         <SelectItem value="approved">Approved</SelectItem>
          //       </SelectContent>
          //     </Select>
          //   </div>
          // }
        />
      )}

    </div>
  );
};

export default TermSheetHistory;
